import { Alert, Anchor, Box, Code, Divider, Image, List, Paper, Stack, Text, Title } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import type { ReactNode } from "react";
import powerBiApplyClose from "../../assets/mcbimDocumentation/powerbi-apply-close.png";
import powerBiConnectionUri from "../../assets/mcbimDocumentation/powerbi-connection-uri.png";
import powerBiExpandProperties from "../../assets/mcbimDocumentation/powerbi-expand-properties.png";
import powerBiExpandPsets from "../../assets/mcbimDocumentation/powerbi-expand-psets.png";
import powerBiFileIsLatest from "../../assets/mcbimDocumentation/powerbi-file-is-latest.png";
import powerBiGetData from "../../assets/mcbimDocumentation/powerbi-get-data.png";
import powerBiNavigator from "../../assets/mcbimDocumentation/powerbi-navigator.png";
import powerBiOdbcDriver from "../../assets/mcbimDocumentation/powerbi-odbc-driver.png";
import powerBiParseJson from "../../assets/mcbimDocumentation/powerbi-parse-json.png";

const MCBIM_URI =
  "mongodb://atlas-sql-6413605a901a56187a646b45-fycix.a.query.mongodb.net/mcbim?ssl=true&authSource=admin";

type DocumentationStep = {
  title: string;
  description: ReactNode;
  image?: string;
  imageAlt?: string;
};

const documentationSteps: DocumentationStep[] = [
  {
    title: "Installer ODBC-driver",
    description: (
      <Text size="sm">
        Last ned og installer siste ODBC-driver fra{" "}
        <Anchor href="https://www.mongodb.com/try/download/odbc-driver" target="_blank" rel="noreferrer">
          MongoDB
        </Anchor>
        .
      </Text>
    ),
    image: powerBiOdbcDriver,
    imageAlt: "Nedlasting av MongoDB ODBC-driver",
  },
  {
    title: "Koble til MongoDB Atlas SQL i Power BI",
    description:
      "Start Power BI. Gå til Get Data (Hent data), søk opp MongoDB Atlas SQL og trykk Connect.",
    image: powerBiGetData,
    imageAlt: "Power BI Get Data med MongoDB Atlas SQL valgt",
  },
  {
    title: "Legg inn tilkoblingsinformasjon",
    description: (
      <Stack gap="xs">
        <Text size="sm">Bruk disse verdiene i tilkoblingsdialogen:</Text>
        <Paper withBorder p="sm" radius="sm" bg="gray.0">
          <Stack gap={4}>
            <Text size="sm">
              <Text span fw={700}>
                MongoDB URI:
              </Text>{" "}
              <Code>{MCBIM_URI}</Code>
            </Text>
            <Text size="sm">
              <Text span fw={700}>
                Database:
              </Text>{" "}
              <Code>mcbim</Code>
            </Text>
          </Stack>
        </Paper>
      </Stack>
    ),
    image: powerBiConnectionUri,
    imageAlt: "Power BI MongoDB URI og database",
  },
  {
    title: "Få brukernavn og passord",
    description: (
      <Text size="sm">
        Trenger du brukernavn og passord? Send e-post til{" "}
        <Anchor href="mailto:mcapps@multiconsult.no">mcapps@multiconsult.no</Anchor>.
      </Text>
    ),
  },
  {
    title: "Velg tabell fra oppdraget",
    description:
      "I Navigator velger du mcbim og en tabell fra ditt oppdrag. Tabellen er merket med oppdragsnummer. Trykk Transform data.",
    image: powerBiNavigator,
    imageAlt: "Power BI Navigator med mcbim-tabeller",
  },
  {
    title: "Filtrer på siste filversjon",
    description:
      "Gå til kolonnen fileIsLatest og sett filteret til true. Da tas bare siste versjon av filene med i dashboardet, slik at ikke all historikk lastes inn.",
    image: powerBiFileIsLatest,
    imageAlt: "Power BI-filter på fileIsLatest true",
  },
  {
    title: "Begrens datamengden ved behov",
    description:
      "Sett gjerne filter på filnavn eller andre relevante felt dersom du ikke trenger alle data inn i dashboardet. Det reduserer tiden det tar å laste inn data.",
  },
  {
    title: "Dekod IFC-egenskaper fra psets",
    description:
      "IFC-enes egenskaper ligger i kolonnen psets, som er lagret i JSON-format. Kolonnen må derfor dekodes ved å kjøre Parse: JSON.",
    image: powerBiParseJson,
    imageAlt: "Power BI Parse JSON på psets-kolonnen",
  },
  {
    title: "Ekspander egenskapssettene",
    description: "Ekspander kolonnen for å vise egenskapssettene. Tips: trykk Load more.",
    image: powerBiExpandPsets,
    imageAlt: "Power BI ekspanderer psets-kolonnen",
  },
  {
    title: "Ekspander egenskapene",
    description:
      "Ekspander hver enkelt kolonne for egenskapssettene slik at egenskapene blir synlige. Tips: trykk Load more.",
    image: powerBiExpandProperties,
    imageAlt: "Power BI ekspanderer egenskaper fra psets",
  },
  {
    title: "Bruk endringene",
    description: "Trykk Apply & close når alle egenskaper er synlige i hver sin kolonne.",
    image: powerBiApplyClose,
    imageAlt: "Power BI Apply & close",
  },
];

const McBimDocumentation: React.FC = () => {
  return (
    <Paper withBorder radius="md" h="calc(100vh - 230px)" miw={0} style={{ overflowY: "auto" }}>
      <Stack gap="lg" maw={1100} p="md" pb="xl" miw={0}>
        <Stack gap={4}>
          <Title order={2}>Power BI-kobling til MCBIM</Title>
          <Text c="dimmed">Oppdatert veiledning for å lese MCBIM-data inn i Power BI.</Text>
        </Stack>

        <Alert color="blue" variant="light" icon={<IconInfoCircle size={18} />}>
          Datakilden er klar til bruk når tabellen er koblet til, filtrert og JSON-egenskapene i <Code>psets</Code> er
          ekspandert.
        </Alert>

        <List type="ordered" spacing="lg">
          {documentationSteps.map((step) => (
            <List.Item key={step.title}>
              <Stack gap="sm">
                <Stack gap={4}>
                  <Title order={4}>{step.title}</Title>
                  {typeof step.description === "string" ? <Text size="sm">{step.description}</Text> : step.description}
                </Stack>

                {step.image != null && (
                  <Paper withBorder radius="md" p="xs" bg="white">
                    <Image src={step.image} alt={step.imageAlt ?? step.title} fit="contain" maw="100%" />
                  </Paper>
                )}
              </Stack>
            </List.Item>
          ))}
        </List>

        <Divider />

        <Alert color="green" variant="light">Datakilden er klar til bruk.</Alert>
      </Stack>
    </Paper>
  );
};

export default McBimDocumentation;
